import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, Clock, FileText, Eye, Edit, 
  Send, MessageSquare, ArrowLeft, RefreshCw, User, Calendar
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  published: { label: 'Publicado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function ArticleReview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchPendingArticles();
  }, []);

  const fetchPendingArticles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/articles/pending-review');
      setArticles(res.data.articles || []);
    } catch (err) {
      console.error('Error fetching articles:', err);
      toast.error('Erro ao carregar artigos pendentes');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticleDetails = async (id) => {
    try {
      const res = await api.get(`/articles/${id}`);
      setSelectedArticle(res.data);
    } catch (err) {
      toast.error('Erro ao carregar detalhes do artigo');
    }
  };

  const handleApprove = async () => {
    if (!selectedArticle) return;
    setProcessingAction(true);
    try {
      await api.post(`/articles/${selectedArticle.id}/approve${actionNotes ? `?notes=${encodeURIComponent(actionNotes)}` : ''}`);
      toast.success('Artigo aprovado e publicado');
      setActionModal(null);
      setActionNotes('');
      setSelectedArticle(null);
      fetchPendingArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar artigo');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!selectedArticle || !actionNotes.trim()) {
      toast.error('Por favor, indique o motivo da rejeição');
      return;
    }
    setProcessingAction(true);
    try {
      await api.post(`/articles/${selectedArticle.id}/reject?reason=${encodeURIComponent(actionNotes)}`);
      toast.success('Artigo rejeitado');
      setActionModal(null);
      setActionNotes('');
      setSelectedArticle(null);
      fetchPendingArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rejeitar artigo');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!selectedArticle || !actionNotes.trim()) {
      toast.error('Por favor, descreva as alterações necessárias');
      return;
    }
    setProcessingAction(true);
    try {
      await api.post(`/articles/${selectedArticle.id}/request-changes?changes=${encodeURIComponent(actionNotes)}`);
      toast.success('Alterações solicitadas ao autor');
      setActionModal(null);
      setActionNotes('');
      setSelectedArticle(null);
      fetchPendingArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar alterações');
    } finally {
      setProcessingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="article-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Revisão de Artigos</h1>
          <p className="text-sm text-gray-500 mt-1">
            {articles.length} artigo(s) pendente(s) de aprovação
          </p>
        </div>
        <button
          onClick={fetchPendingArticles}
          className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={18} className="text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Articles List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Clock size={16} className="text-amber-500" />
              Artigos Pendentes
            </h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {articles.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <CheckCircle size={40} className="mx-auto mb-3 opacity-30" />
                <p>Nenhum artigo pendente</p>
              </div>
            ) : (
              articles.map(article => (
                <button
                  key={article.id}
                  onClick={() => fetchArticleDetails(article.id)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                    selectedArticle?.id === article.id ? 'bg-red-50 border-l-4 border-[#CC0000]' : ''
                  }`}
                  data-testid={`review-article-${article.id}`}
                >
                  <p className="font-medium text-gray-900 line-clamp-2">{article.title}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <User size={12} />
                    <span>{article.author_name}</span>
                    <span>•</span>
                    <Calendar size={12} />
                    <span>{new Date(article.created_at).toLocaleDateString('pt-PT')}</span>
                  </div>
                  {article.category_name && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                      {article.category_name}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Article Preview */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {selectedArticle ? (
            <>
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Pré-visualização</h3>
                <div className="flex items-center gap-2">
                  <a
                    href={`/noticias/${selectedArticle.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Ver página completa"
                  >
                    <Eye size={16} />
                  </a>
                  <button
                    onClick={() => navigate(`/admin/artigos/${selectedArticle.id}/editar`)}
                    className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                    title="Editar artigo"
                  >
                    <Edit size={16} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 max-h-[400px] overflow-y-auto">
                {selectedArticle.featured_image && (
                  <img 
                    src={selectedArticle.featured_image} 
                    alt={selectedArticle.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedArticle.title}</h2>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <User size={14} />
                    {selectedArticle.author_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(selectedArticle.created_at).toLocaleDateString('pt-PT')}
                  </span>
                  {selectedArticle.category_name && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                      {selectedArticle.category_name}
                    </span>
                  )}
                </div>
                {selectedArticle.excerpt && (
                  <p className="text-gray-600 italic mb-4 border-l-4 border-amber-400 pl-4">
                    {selectedArticle.excerpt}
                  </p>
                )}
                <div 
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-wrap gap-2">
                <button
                  onClick={() => setActionModal('approve')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                  data-testid="approve-article-btn"
                >
                  <CheckCircle size={16} />
                  Aprovar e Publicar
                </button>
                <button
                  onClick={() => setActionModal('changes')}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600"
                  data-testid="request-changes-btn"
                >
                  <MessageSquare size={16} />
                  Solicitar Alterações
                </button>
                <button
                  onClick={() => setActionModal('reject')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                  data-testid="reject-article-btn"
                >
                  <XCircle size={16} />
                  Rejeitar
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-400">
              <div className="text-center">
                <FileText size={48} className="mx-auto mb-3 opacity-30" />
                <p>Selecione um artigo para pré-visualizar</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-5 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">
                {actionModal === 'approve' && 'Aprovar Artigo'}
                {actionModal === 'changes' && 'Solicitar Alterações'}
                {actionModal === 'reject' && 'Rejeitar Artigo'}
              </h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                {actionModal === 'approve' && 'O artigo será publicado imediatamente. Adicione notas opcionais para o autor:'}
                {actionModal === 'changes' && 'Descreva as alterações necessárias para o autor:'}
                {actionModal === 'reject' && 'Indique o motivo da rejeição:'}
              </p>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder={
                  actionModal === 'approve' ? 'Notas opcionais...' :
                  actionModal === 'changes' ? 'Descreva as alterações necessárias...' :
                  'Motivo da rejeição...'
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 min-h-[100px]"
                required={actionModal !== 'approve'}
              />
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setActionModal(null); setActionNotes(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                disabled={processingAction}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (actionModal === 'approve') handleApprove();
                  else if (actionModal === 'changes') handleRequestChanges();
                  else if (actionModal === 'reject') handleReject();
                }}
                disabled={processingAction}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  actionModal === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  actionModal === 'changes' ? 'bg-amber-500 hover:bg-amber-600' :
                  'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
                data-testid="confirm-action-btn"
              >
                {processingAction ? 'A processar...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
